import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { onThemeChange, readIsDark } from './telegram';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'multicrm.theme';

interface ThemeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // хранилище недоступно — остаёмся на системной теме
  }
  return 'system';
}

/**
 * Тема.
 *
 * По умолчанию приложение следует за Telegram: клиент сам присылает цвета
 * светлой или тёмной темы, и переключение в Telegram меняет интерфейс без
 * перезагрузки. Режимы «светлая» и «тёмная» — осознанный выбор пользователя,
 * он переживает перезапуск.
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [mode, setModeState] = useState<ThemeMode>(readMode);
  const [systemDark, setSystemDark] = useState<boolean>(readIsDark);

  useEffect(() => onThemeChange(setSystemDark), []);

  const isDark = mode === 'system' ? systemDark : mode === 'dark';

  useEffect(() => {
    // Единственный переключатель темы — атрибут на <html>, дальше работает CSS.
    document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
  }, [isDark]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // не критично: режим действует до перезапуска
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, isDark, setMode }),
    [mode, isDark, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme вне ThemeProvider');
  return context;
}
